import { Link } from 'react-router-dom';
import { PartyBuilder } from '@/components/events/PartyBuilder';
import { AppSelect } from '@/components/shared/AppSelect';
import { Button } from '@/components/shared/Button';
import { EventStatusBadge } from '@/components/shared/EventStatusBadge';
import { EventStatusPicker } from '@/components/shared/EventStatusPicker';
import { Panel } from '@/components/shared/Panel';
import { templateRoutes } from '@/data/routes';
import { addSlotToParty, moveSlotWithinRoleGroup, normalizePartySlots } from '@/features/events/builder';
import type { TemplateSlotMetrics } from '@/features/templates/builder';
import type { EventBuilderDraft, EventBuilderPartyDraft, PartyTemplate, WeaponCatalogItem } from '@/types/domain';
import { formatDateTime } from '@/utils/date';

interface EventBuilderFormProps {
  mode: 'create' | 'edit';
  draft: EventBuilderDraft;
  templates: PartyTemplate[];
  weapons: WeaponCatalogItem[];
  joinedCountBySlotId: Record<string, number>;
  isSaving: boolean;
  isArchiving: boolean;
  isDeleting: boolean;
  canManageTemplates: boolean;
  canDeletePermanently: boolean;
  timeZoneLabel: string;
  selectedTemplate: PartyTemplate | null;
  selectedTemplateMetrics: TemplateSlotMetrics | null;
  isTemplateLayoutApplied: boolean;
  onDraftChange: (draft: EventBuilderDraft) => void;
  onTemplateChange: (templateId?: string) => void;
  onApplyTemplateLayout: () => void;
  onQuickCreateFromTemplate: () => Promise<void>;
  onSave: () => Promise<void>;
  onSaveAsTemplate?: () => void;
  onArchive?: () => Promise<void>;
  onDelete?: () => void;
}

function updatePartyDraft(
  draft: EventBuilderDraft,
  partyNumber: number,
  updater: (party: EventBuilderPartyDraft) => EventBuilderPartyDraft,
): EventBuilderDraft {
  return {
    ...draft,
    parties: draft.parties.map((party) => (party.partyNumber === partyNumber ? updater(party) : party)),
  };
}

function renderTemplateMetrics(metrics: TemplateSlotMetrics): JSX.Element {
  return (
    <div className="template-metrics">
      <div className="template-metrics__item">
        <strong>{metrics.configuredSlotCount}</strong>
        <span>{metrics.configuredSlotCount === 1 ? 'Configured Slot' : 'Configured Slots'}</span>
      </div>
      <div className="template-metrics__item">
        <strong>{metrics.activeSlotCount}</strong>
        <span>{metrics.activeSlotCount === 1 ? 'Active Slot' : 'Active Slots'}</span>
      </div>
      <div className="template-metrics__item">
        <strong>{metrics.totalRequired}</strong>
        <span>Total Required</span>
      </div>
      <div className="template-metrics__item">
        <strong>{metrics.configuredPartyCount}</strong>
        <span>{metrics.configuredPartyCount === 1 ? 'Party Used' : 'Parties Used'}</span>
      </div>
    </div>
  );
}

export function EventBuilderForm({
  mode,
  draft,
  templates,
  weapons,
  joinedCountBySlotId,
  isSaving,
  isArchiving,
  isDeleting,
  canManageTemplates,
  canDeletePermanently,
  timeZoneLabel,
  selectedTemplate,
  selectedTemplateMetrics,
  isTemplateLayoutApplied,
  onDraftChange,
  onTemplateChange,
  onApplyTemplateLayout,
  onQuickCreateFromTemplate,
  onSave,
  onSaveAsTemplate,
  onArchive,
  onDelete,
}: EventBuilderFormProps): JSX.Element {
  const hasTemplateSelection = Boolean(draft.templateId && selectedTemplate);
  const templateOptions = templates
    .filter((template) => template.isActive || template.id === draft.templateId)
    .map((template) => ({
      value: template.id,
      label: template.name,
      description: template.isActive ? 'Saved layout ready for mass creation' : 'Archived template',
      disabled: !template.isActive && template.id !== draft.templateId,
    }));

  return (
    <div className="stack">
      <Panel
        title={mode === 'create' ? 'Create Mass' : 'Edit Mass'}
        description={`Set the timing, optionally pull in a saved template, then shape Party 1 through Party 4. All times are shown in ${timeZoneLabel}.`}
        action={
          <div className="panel-actions">
            {canManageTemplates && onSaveAsTemplate ? (
              <Button variant="secondary" onClick={onSaveAsTemplate} disabled={isSaving}>
                Save as Template
              </Button>
            ) : null}
            {mode === 'edit' && onArchive ? (
              <Button variant="secondary" onClick={() => void onArchive()} disabled={isArchiving || isDeleting}>
                {isArchiving ? 'Archiving...' : 'Archive Mass'}
              </Button>
            ) : null}
            {mode === 'edit' && canDeletePermanently && onDelete ? (
              <Button variant="danger" onClick={onDelete} disabled={isDeleting || isArchiving}>
                {isDeleting ? 'Deleting...' : 'Delete Mass'}
              </Button>
            ) : null}
            <Button onClick={() => void onSave()} disabled={isSaving}>
              {isSaving ? 'Saving...' : mode === 'create' ? 'Create Mass' : 'Save Mass'}
            </Button>
          </div>
        }
      >
        <div className="form-grid form-grid--two">
          <label className="field">
            <span className="field__label">Mass Title</span>
            <input
              className="field__input"
              value={draft.title}
              onChange={(event) => onDraftChange({ ...draft, title: event.target.value })}
              placeholder="Prime Time ZvZ Push"
            />
          </label>

          <label className="field">
            <span className="field__label">Template</span>
            <AppSelect
              ariaLabel="Mass template"
              value={draft.templateId}
              options={[
                {
                  value: '__none__',
                  label: 'No template selected',
                  description: 'Build the mass layout manually',
                },
                ...templateOptions,
              ]}
              onChange={(value) => onTemplateChange(value === '__none__' ? undefined : value)}
              placeholder="Choose a saved template"
            />
          </label>
        </div>

        {selectedTemplate ? (
          <div className={`template-selection-card ${isTemplateLayoutApplied ? 'template-selection-card--applied' : 'template-selection-card--pending'}`}>
            <div className="template-selection-card__header">
              <div>
                <span className="profile-summary__label">Selected Template</span>
                <strong>{selectedTemplate.name}</strong>
                <span className="profile-summary__hint">
                  {isTemplateLayoutApplied
                    ? 'Saved layout is already loaded into Party 1 to Party 4 below.'
                    : 'Template selected. Apply the saved layout to populate the builder below before saving this mass.'}
                </span>
              </div>
              <div className="template-selection-card__badges">
                <EventStatusBadge status={draft.status} size="sm" />
                <span className={`template-selection-state ${isTemplateLayoutApplied ? 'template-selection-state--success' : 'template-selection-state--warning'}`}>
                  {isTemplateLayoutApplied ? 'Layout Applied' : 'Layout Not Applied'}
                </span>
              </div>
            </div>

            {selectedTemplateMetrics ? renderTemplateMetrics(selectedTemplateMetrics) : null}

            <div className="panel-actions">
              <Button variant="secondary" onClick={onApplyTemplateLayout} disabled={isSaving}>
                {isTemplateLayoutApplied ? 'Reapply Saved Layout' : 'Apply Template Layout'}
              </Button>
              {mode === 'create' ? (
                <Button variant="secondary" onClick={() => void onQuickCreateFromTemplate()} disabled={isSaving}>
                  Create Directly From Saved Template
                </Button>
              ) : null}
              {canManageTemplates ? (
                <Link to={templateRoutes.edit(selectedTemplate.id)} className="button button--ghost">
                  Edit Template
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}

        <label className="field">
          <span className="field__label">Description</span>
          <textarea
            className="field__input field__input--textarea"
            value={draft.description}
            onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
            placeholder="Shotcaller notes, map focus, regroup expectations, and any comp reminders."
          />
        </label>

        <div className="form-grid form-grid--event-timing">
          <div className="field">
            <span className="field__label">Mass State</span>
            <EventStatusPicker
              value={draft.status}
              disabled={isSaving || isArchiving || isDeleting}
              onChange={(status) => onDraftChange({ ...draft, status })}
            />
          </div>

          <label className="field">
            <span className="field__label">Signup Opens</span>
            <input
              className="field__input"
              type="datetime-local"
              value={draft.signupOpensAt}
              onChange={(event) => onDraftChange({ ...draft, signupOpensAt: event.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Signup Closes</span>
            <input
              className="field__input"
              type="datetime-local"
              value={draft.signupClosesAt}
              onChange={(event) => onDraftChange({ ...draft, signupClosesAt: event.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Start Time</span>
            <input
              className="field__input"
              type="datetime-local"
              value={draft.startsAt}
              onChange={(event) => onDraftChange({ ...draft, startsAt: event.target.value })}
            />
          </label>
        </div>

        <div className="profile-summary">
          <div>
            <span className="profile-summary__label">Signup Window</span>
            <strong>{draft.signupOpensAt ? formatDateTime(draft.signupOpensAt) : 'Choose an open time'}</strong>
            <span className="profile-summary__hint">
              Closes {draft.signupClosesAt ? formatDateTime(draft.signupClosesAt) : 'after you set the time'}
            </span>
          </div>
          <div>
            <span className="profile-summary__label">Mass Start</span>
            <strong>{draft.startsAt ? formatDateTime(draft.startsAt) : 'Choose a start time'}</strong>
            <span className="profile-summary__hint">
              <EventStatusBadge status={draft.status} size="sm" /> {timeZoneLabel}
            </span>
          </div>
        </div>
      </Panel>

      {draft.parties.map((party) => (
        <PartyBuilder
          key={party.partyNumber}
          party={party}
          weapons={weapons}
          joinedCountBySlotId={joinedCountBySlotId}
          onLabelChange={(label) => onDraftChange(updatePartyDraft(draft, party.partyNumber, (current) => ({ ...current, label })))}
          onAddSlot={() => onDraftChange(addSlotToParty(draft, party.partyNumber))}
          onRemoveSlot={(clientId) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: normalizePartySlots(current.slots.filter((slot) => slot.clientId !== clientId)),
              })),
            )
          }
          onMoveSlot={(clientId, direction) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: moveSlotWithinRoleGroup(current.slots, clientId, direction),
              })),
            )
          }
          onChangeSlot={(clientId, patch) =>
            onDraftChange(
              updatePartyDraft(draft, party.partyNumber, (current) => ({
                ...current,
                slots: normalizePartySlots(current.slots.map((slot) => (slot.clientId === clientId ? { ...slot, ...patch } : slot))),
              })),
            )
          }
        />
      ))}
    </div>
  );
}
